import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

/**
 * ConfirmDialog — dialog di conferma coerente col design system, in
 * sostituzione dei window.confirm/window.prompt nativi.
 *
 * Props:
 *   - open        : bool
 *   - title       : string
 *   - desc        : string | node — spiegazione di cosa succede
 *   - confirmLabel: string (default "Conferma")
 *   - cancelLabel : string (default "Annulla")
 *   - danger      : bool — stile distruttivo (rosso)
 *   - typeToConfirm: string | null — se valorizzato, l'utente deve digitare
 *     esattamente questa stringa per abilitare la conferma (azioni distruttive)
 *   - onConfirm() / onCancel()
 *   - testid      : string
 */
export default function ConfirmDialog({
  open,
  title,
  desc,
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  danger = false,
  typeToConfirm = null,
  onConfirm,
  onCancel,
  testid = 'confirm-dialog',
}) {
  const [typed, setTyped] = useState('');
  const panelRef = useRef(null);
  const previousFocus = useRef(null);

  // Reset input a ogni apertura + focus trap con restore
  useEffect(() => {
    if (!open) return undefined;
    setTyped('');
    previousFocus.current = document.activeElement;
    const panel = panelRef.current;
    const focusables = () =>
      panel ? [...panel.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')] : [];
    // Focus iniziale sul primo elemento utile
    const first = focusables()[0];
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (!els.length) return;
      const idx = els.indexOf(document.activeElement);
      if (e.shiftKey && (idx <= 0)) {
        e.preventDefault();
        els[els.length - 1].focus();
      } else if (!e.shiftKey && (idx === els.length - 1 || idx === -1)) {
        e.preventDefault();
        els[0].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previousFocus.current?.focus) previousFocus.current.focus();
    };
  }, [open, onCancel]);

  if (!open) return null;

  const confirmEnabled = !typeToConfirm || typed === typeToConfirm;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-6"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="absolute inset-0 bg-ink/40" aria-hidden="true" />
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="relative w-full max-w-md bg-paper border border-line shadow-xl p-6"
        data-testid={testid}
      >
        <div className="flex items-start gap-3 mb-3">
          {danger && <AlertTriangle size={18} strokeWidth={1.5} className="text-risk mt-0.5 shrink-0" />}
          <h2 id="confirm-dialog-title" className="font-display text-lg tracking-tight">
            {title}
          </h2>
        </div>
        {desc && (
          <p className="font-body text-sm text-soft leading-relaxed mb-4">{desc}</p>
        )}
        {typeToConfirm && (
          <div className="mb-4">
            <label className="mono-label block mb-1" htmlFor="confirm-dialog-input">
              Digita «{typeToConfirm}» per confermare
            </label>
            <input
              id="confirm-dialog-input"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full border border-line bg-canvas px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
              data-testid={`${testid}-input`}
              autoComplete="off"
            />
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-line text-soft hover:border-ink hover:text-ink font-mono text-xs uppercase tracking-[0.18em] transition-colors"
            data-testid={`${testid}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => confirmEnabled && onConfirm?.()}
            disabled={!confirmEnabled}
            className={
              'px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
              (danger
                ? 'bg-risk text-white hover:bg-risk/80'
                : 'bg-ink text-canvas hover:bg-accent')
            }
            data-testid={`${testid}-confirm`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
